import semver from 'semver';

import type { HomeyInfo } from './SelectedHomeyStore.types';

export class HomeyUtils {
  // Advanced Flow is available as of Homey v8.0.0
  // but due to issues with semver checking versions
  // with suffixes (e.g. 8.0.1-rc.2) we must check for
  // greater than the version without Advanced Flow (v7.4.3).
  static ADVANCED_FLOW_VERSION = '7.4.3';
  static MOODS_VERSION_LOCAL_PLATFORM_V2 = '10.3.3';
  static MOODS_VERSION_LOCAL_PLATFORM_V1 = '11.0.0';
  static SEAMLESS_DEVICE_PAIRING_SCREENS_VERSION = '8.1.0';
  static HIDDEN_DEVICES_VERSION = '12.13.0';
  static VIDEO_STREAMING_VERSION = '12.12.0';
  static ZONE_SORTING_VERSION = '12.12.0';
  static DEVICE_QUICK_ACTION_OVERRIDE_VERSION = '13.2.0';
  static DEVICE_UPDATES_VERSION = '13.2.0';

  static getHasAdvancedFlowVersion(homey: HomeyInfo) {
    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return semver.gt(homey.softwareVersion, `${HomeyUtils.ADVANCED_FLOW_VERSION}`);
  }

  static getHasMoods(homey: HomeyInfo) {
    if (this.getIsCloud(homey)) {
      return true;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    if (
      homey.apiVersion === 2 &&
      semver.gte(version, `${HomeyUtils.MOODS_VERSION_LOCAL_PLATFORM_V1}`)
    ) {
      return true;
    }

    return (
      homey.apiVersion >= 3 && semver.gte(version, `${HomeyUtils.MOODS_VERSION_LOCAL_PLATFORM_V2}`)
    );
  }

  static getHasAdvancedFlowLicense(homey: HomeyInfo | null | undefined) {
    if (homey?.licenses.includes('advanced-flow')) {
      return true;
    }

    if (homey?.platform === 'local' && homey?.platformVersion === 2) {
      return true;
    }

    return false;
  }

  static getIsCloud(homey: HomeyInfo | null | undefined) {
    return homey?.platform === 'cloud';
  }

  static getIsSeamlessDevicePairingScreens(homey: HomeyInfo) {
    if (this.getIsCloud(homey)) {
      return true;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return semver.gte(version, HomeyUtils.SEAMLESS_DEVICE_PAIRING_SCREENS_VERSION);
  }

  static getHasDeviceQuickActionOverride(homey: HomeyInfo | null | undefined) {
    if (this.getIsCloud(homey)) {
      return true;
    }

    if (homey == null) {
      return false;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return semver.gte(version, HomeyUtils.DEVICE_QUICK_ACTION_OVERRIDE_VERSION);
  }

  static getHasHiddenDevices(homey: HomeyInfo | null | undefined) {
    if (this.getIsCloud(homey)) {
      return true;
    }

    if (homey == null) {
      return false;
    }

    if (homey.platformVersion < 2) {
      return false;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return semver.gte(version, HomeyUtils.HIDDEN_DEVICES_VERSION);
  }

  static getHasDeviceUpdates(homey: HomeyInfo | null | undefined) {
    if (homey == null) {
      return false;
    }

    if (homey.platform === 'cloud') {
      return true;
    }

    if (homey.platform !== 'local' || homey.platformVersion < 2) {
      return false;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return semver.gte(version, HomeyUtils.DEVICE_UPDATES_VERSION);
  }

  static getHasVideoStreaming(homey: HomeyInfo | null | undefined) {
    if (homey?.platform !== 'local') {
      return false;
    }

    if (homey.apiVersion < 3) {
      return false;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return semver.gte(version, HomeyUtils.VIDEO_STREAMING_VERSION);
  }

  static getHasZoneSorting(homey: HomeyInfo | null | undefined) {
    if (homey == null) {
      return false;
    }

    if (this.getIsCloud(homey)) {
      return true;
    }

    const version = semver.coerce(homey.softwareVersion);

    if (version == null) {
      return false;
    }

    return (
      homey.platform === 'local' &&
      homey.platformVersion === 2 &&
      semver.gte(version, HomeyUtils.ZONE_SORTING_VERSION)
    );
  }
}
